import { zodResolver } from '@hookform/resolvers/zod'
import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import Button from '@repo/ui/button'
import { DeleteIcon } from '@repo/ui/icons'
import Modal from '@repo/ui/modal'
import Typography from '@repo/ui/typography'
import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { z, ZodType } from 'zod'

import { getCountries } from '../../../api/countries/get-countries'
import { useModalManager } from '../../../lib/hooks/use-modal-manager'
import useTranslate from '../../../lib/hooks/use-translate'
import {
  StaffPositionEnum,
  staffPositionEnumMapping
} from '../../../lib/types/enums/staff/staff-position.enum'
import {
  StaffQualificationEnum,
  staffQualificationEnumMapping
} from '../../../lib/types/enums/staff/staff-qualifications.enum'
import { ContractTemplateInput } from '../../../lib/types/models/contract-template/contract-template-input'
import { BaseFormProps } from '../../../lib/types/utils'
import { getSubmitAction } from '../../../lib/utils/buttons-config/validation-buttons-config'
import { getSelectedEnumOption } from '../../../lib/utils/functions/misc.functions'
import {
  createEnumArraySchema,
  createEnumSchema
} from '../../../lib/utils/validations/enum-validation'
import { createNumberArraySchema } from '../../../lib/utils/validations/int-array-validation'
import { RICH_TEXT_SCHEMA } from '../../../lib/utils/validations/rich-text.validation'
import { createStringSchema } from '../../../lib/utils/validations/string-validation'
import { FORM_MODE } from '../../../utils/constants/form.constants'
import { Form, FormControl, FormField, FormItem } from '../../elements/forms/form'
import ControlledInput from '../../elements/inputs/controlled-input'
import ControlledMultiSelectInput from '../../elements/inputs/controlled-multi-select-input'
import ControlledSelectInput from '../../elements/inputs/controlled-select-input'
import ControlledTextEditor from '../../elements/inputs/controlled-text-editor'
import ValidationButtons from '../../elements/validation-buttons'

type Props = BaseFormProps<ContractTemplateInput> & {
  onDelete?: () => Promise<void>
}

const formSchema: ZodType<ContractTemplateInput> = z.object({
  name: createStringSchema('NOM').refine(
    (value) => value.replace(/<[^>]*>?/gm, '').trim().length > 0
  ),
  content: RICH_TEXT_SCHEMA('fr'),
  countryIds: createNumberArraySchema('pays', { minimumNumberOfItems: 1 }),
  staffType: createEnumSchema({ fieldName: 'position', enumType: StaffPositionEnum }),
  staffQualifications: createEnumArraySchema({
    fieldName: 'qualification',
    enumType: StaffQualificationEnum,
    minimumNumberOfItems: 1
  })
})

export function ContractTemplateForm({
  defaultValues,
  onDelete,
  ...validationButtonProps
}: Props) {
  const { t } = useTranslate()
  const [isDeleteLoading, setIsDeleteLoading] = useState(false)
  const { modalState, toggleModal } = useModalManager(['delete'])

  const form = useForm<ContractTemplateInput>({
    resolver: zodResolver(formSchema),
    defaultValues,
    mode: FORM_MODE
  })

  const { data: countries } = useQuery({
    queryKey: ['countries'],
    queryFn: () => getCountries()
  })

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(getSubmitAction(validationButtonProps.buttonsConfig))}
        className={'flex flex-col gap-3'}
      >
        <div className={'flex flex-col gap-3 border rounded-xl p-5 border-grey-light'}>
          <Typography variant={'text'} color={tailwindColors.primary.DEFAULT}>
            Modèle
          </Typography>

          <div className={'grid grid-cols-2 grid-rows-2 gap-2'}>
            <FormField
              control={form.control}
              name={'name'}
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <ControlledInput {...field} type={'text'} label={'NOM du contrat'} required />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name={'staffType'}
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <ControlledSelectInput
                      {...field}
                      required
                      label={'Poste'}
                      options={Object.values(StaffPositionEnum).map((status) => ({
                        label: t(staffPositionEnumMapping[status]),
                        value: status
                      }))}
                      selectedOption={getSelectedEnumOption(
                        field.value,
                        staffPositionEnumMapping,
                        t
                      )}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name={'staffQualifications'}
              render={({ field }) => (
                <FormItem className="w-full">
                  <FormControl>
                    <ControlledMultiSelectInput
                      {...field}
                      variant={'primary'}
                      required
                      searchable
                      label={'Qualifications'}
                      options={Object.values(StaffQualificationEnum).map((qualification) => ({
                        label: t(staffQualificationEnumMapping[qualification].label),
                        value: qualification
                      }))}
                      selectedOptions={field.value?.map((qualification) => ({
                        label: t(staffQualificationEnumMapping[qualification].label),
                        value: qualification
                      }))}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name={'countryIds'}
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <ControlledMultiSelectInput
                      {...field}
                      variant={'primary'}
                      required
                      searchable
                      label={'Pays'}
                      options={
                        countries?.data.map((country) => ({
                          label: country.name,
                          value: country.id
                        })) ?? []
                      }
                      selectedOptions={field.value?.map((id) => ({
                        label: countries?.data.find((country) => country.id === id)?.name ?? '',
                        value: id
                      }))}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>
        </div>

        <div className={'flex flex-col gap-3 border rounded-xl p-5 border-grey-light'}>
          <Typography variant={'text'} color={tailwindColors.grey.DEFAULT}>
            Saisir le texte
          </Typography>

          <FormField
            control={form.control}
            name={'content'}
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <ControlledTextEditor
                    {...field}
                    required
                    placeholder={'Rédigez votre contrat ici'}
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </div>

        <div className={'flex justify-end gap-3'}>
          <ValidationButtons
            {...validationButtonProps}
            preComponent={
              onDelete && (
                <Button
                  type={'button'}
                  variant={'destructive'}
                  label={'Supprimer'}
                  icon={DeleteIcon}
                  onClick={() => toggleModal('delete')}
                />
              )
            }
          />

          <Modal
            actionButtonsOptions={{
              confirmText: 'Confirmer',
              cancelText: 'Fermer',

              onConfirm: async () => {
                setIsDeleteLoading(true)
                await onDelete?.()
                setIsDeleteLoading(false)
                toggleModal('delete')
              }
            }}
            onCancel={() => toggleModal('delete')}
            isOpen={modalState.delete}
            title={'Êtes-vous sûr de vouloir supprimer ce modèle de contrat ?'}
            icon={DeleteIcon}
            isLoading={isDeleteLoading}
            variant={'destructive'}
          />
        </div>
      </form>
    </Form>
  )
}
